import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, Navigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import {
  trackProps,
  withTrack,
  withTrackProps,
} from '@dpdgroupuk/react-event-tracker';
import { compose } from 'redux';

import AppHeader from '../../features/appHeader';
import SubHeader from '../../features/subHeader';
import Mapbox from '../../components/Mapbox';
import SectionMenu from '../../features/leftMenu';
import RouteDetail from '../../features/routeDetail';
import Notifier, { NotificationType } from '../../features/Notifier';
import { PAGE_START, ROUTES_PAGE } from '../../constants/routes';
import RouteListContainer from './components/RouteListContainer';
import SectionListContainer from './components/SectionListContainer';
import Filter from '../filter';
import { View } from '../../components/Base';
import LoadingOverlay from '../../components/LoadingOverlay';
import { isActiveLoadProcess } from '../../store/selectors/loader';
import {
  getRouteDetail,
  isCurrentUserLock,
  isDefaultRoute,
  isRouteStatusDeparted,
  isSaveRouteEnabled,
  isScheduleRequired,
} from '../../store/selectors/route';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import { MAP_BOX, ROUTE_PAGE } from '../../constants/analytics';
import {
  getSelectedDepotLocation,
  isDisasterRecoveryMode,
} from '../../store/selectors/app';
import {
  getDepotsForDropDown,
  getUserLoading,
  getUserUid,
  hasDisasterRecoveryAccess,
} from '../../store/selectors/user';
import {
  handleErrorNotification,
  showNotification,
} from '../../store/actions/notifications';
import {
  DISASTER_RECOVERY_HEADER_MESSAGE,
  DISMISS,
  NO_AVAILABLE_DEPOTS,
} from '../../constants/strings';
import usePolling from '../../hooks/usePolling';
import * as RouteService from '../../services/routes';
import * as SectionService from '../../services/section';
import { successRouteFetch } from '../../store/reducers/routes';
import { successSectionsFetch } from '../../store/reducers/sections';
import {
  LOAD_ROUTES_PROCESS,
  LOAD_SECTIONS_PROCESS,
} from '../../enums/loaderProcess';
import useLoader from '../../hooks/useLoader';
import useDepotChangeListener from '../../hooks/useDepotChangeListener';
import ModalControlled from '../../components/ModalControlled';
import DRWarningModal from '../../components/modals/DRWarningModal';
import palette from '../../components/styles/palette';
import layout from '../../components/styles/layout';
import DepotMapLayer from '../../components/DepotMapLayer';
import StopsMapLayer from '../../components/StopsMapLayer';
import useRouteChangeListener from '../../hooks/useRouteChangeListener';
import ToggleRouteDetailButton from './components/ToggleRouteDetailButton';
import ModalsContainer from './components/ModalsContainer';
import { setZoomLevel } from '../../store/reducers/map';
import RouteLeavingGuard from './components/RouteLeavingGuard';
import { parseQuery } from '../../utils/query';
import { RouteManagementButtons } from './components/RouteManagementButtons';
import ScheduleRoute from '../../features/scheduleRoute';
import { getMapFitBounds } from '../../store/selectors/ui/routeDetail';
import { DEFAULT_ZOOM } from '../../store/selectors/map';
import { clearStopFilters } from '../../store/reducers/ui/filterPanel';
import { serializeDateObject } from '../../models/route';
import Header from '../../components/header/Header';
import DriverMapLayer from '../../components/DriverMapLayer';

const useStyles = makeStyles(({ typography }) => {
  const { pxToRem } = typography;
  return {
    appContainer: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    content: {
      flexGrow: 1,
      position: 'relative',
      display: 'flex',
      overflow: 'hidden',
    },
    mapbox: {
      position: 'absolute',
      zIndex: 1,
      height: '100%',
      width: '100%',
      flex: 1,
      top: 0,
    },
    left: {
      position: 'relative',
      display: 'flex',
    },
    headerMessage: {
      backgroundColor: palette.slate,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: pxToRem(layout.subheader.height),
      color: palette.typography.white.solid,
    },
  };
});

const PlannerPage = ({ onMapLoad }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const disasterRecoveryWarningRef = useRef();
  useDepotChangeListener(disasterRecoveryWarningRef);
  const [map, setMap] = useState();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const isLoadProcess = useSelector(isActiveLoadProcess);
  const routeDetail = useSelector(getRouteDetail);
  const currentUserLock = useSelector(isCurrentUserLock);
  const depots = useSelector(getDepotsForDropDown);
  const isUserLoading = useSelector(getUserLoading);
  const userUid = useSelector(getUserUid);
  const { coordinates, zoom } = useSelector(getSelectedDepotLocation);
  const fitBounds = useSelector(getMapFitBounds);
  const disasterRecoveryAccess = useSelector(hasDisasterRecoveryAccess);
  const isDRmode = useSelector(isDisasterRecoveryMode);
  const saveRouteEnable = useSelector(isSaveRouteEnabled);
  const isRouteDefault = useSelector(isDefaultRoute);
  const scheduleRequired = useSelector(isScheduleRequired);
  const isRouteDeparted = useSelector(isRouteStatusDeparted);

  const [showLoader, hideLoader] = useLoader();
  const { routeId, detailVisible, date, depot, sectionId } =
    useLocationQueryParams();

  const isRouteDataLoaded = useMemo(
    () => routeId && routeDetail,
    [routeId, routeDetail]
  );

  const showRouteDetail = useMemo(
    () => isRouteDataLoaded && detailVisible,
    [detailVisible, isRouteDataLoaded]
  );

  const showDriverRouteOnMap = useMemo(
    () =>
      process.env.REACT_APP_ENABLE_DRIVER_EVENTS &&
      isRouteDataLoaded &&
      !detailVisible &&
      isRouteDeparted,
    [detailVisible, isRouteDataLoaded, isRouteDeparted]
  );

  const handleOnCenterMap = useCallback(() => {
    const fitBoundsOptions = {
      padding: { top: 80, bottom: 80, left: 150, right: 5 },
    };
    map &&
      map.fitBounds(
        fitBounds,
        isRouteDataLoaded
          ? fitBoundsOptions
          : { ...fitBoundsOptions, maxZoom: DEFAULT_ZOOM }
      );
  }, [fitBounds, map, isRouteDataLoaded]);

  useRouteChangeListener(handleOnCenterMap);

  usePolling(
    depot ? [depot, sectionId, date] : null,
    ([depot, sectionId, date]) =>
      RouteService.getRoutes(depot, sectionId, date),
    {
      refreshInterval: 40000,
      onSuccess: (data) => {
        const dataToSave = {
          ...data,
          routes: data.routes.map((route) => serializeDateObject(route)),
        };

        dispatch(successRouteFetch(dataToSave));
        hideLoader(LOAD_ROUTES_PROCESS);
      },
      onError: (error) => {
        !showRouteDetail && dispatch(handleErrorNotification(error));
        hideLoader(LOAD_ROUTES_PROCESS);
      },
    }
  );

  usePolling(
    depot ? [depot, date] : null,
    ([depot, date]) => SectionService.getSection(depot, date),
    {
      refreshInterval: 20000,
      onSuccess: (data) => {
        dispatch(successSectionsFetch(data));
        hideLoader(LOAD_SECTIONS_PROCESS);
      },
      onError: (error) => {
        !showRouteDetail && dispatch(handleErrorNotification(error));
        hideLoader(LOAD_SECTIONS_PROCESS);
      },
    }
  );

  const isUnsavedData = useMemo(
    () =>
      currentUserLock &&
      (saveRouteEnable || (scheduleRequired && !isRouteDefault)),
    [currentUserLock, isRouteDefault, saveRouteEnable, scheduleRequired]
  );

  useEffect(() => {
    dispatch(setZoomLevel(zoom));
  }, [zoom, dispatch]);

  useEffect(() => {
    if (depot) {
      showLoader(LOAD_ROUTES_PROCESS);
      showLoader(LOAD_SECTIONS_PROCESS);
    }
  }, [date, depot, sectionId, dispatch, showLoader]);

  useEffect(() => {
    if (userUid && !isUserLoading && !depots.length) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: NO_AVAILABLE_DEPOTS,
          buttonText: DISMISS,
          key: 'no-available-depots',
        })
      );
      hideLoader(LOAD_SECTIONS_PROCESS);
      hideLoader(LOAD_ROUTES_PROCESS);
    }
  }, [depots, hideLoader, dispatch, isUserLoading]);

  useEffect(() => {
    showRouteDetail && dispatch(clearStopFilters());
  }, [showRouteDetail, dispatch]);

  const shouldBlockNavigation = useCallback(
    (nextLocation) => {
      const { search } = nextLocation;
      if (isUnsavedData) {
        const {
          routeId: nextRoute,
          date: nextDate,
          depot: nextDepot,
        } = parseQuery(search);
        return (
          routeId !== nextRoute || date !== nextDate || depot !== nextDepot
        );
      }
      return false;
    },
    [routeId, date, depot, isUnsavedData]
  );

  const handleOnMapLoad = useCallback(
    (m) => {
      setMap(m);
      onMapLoad();
    },
    [onMapLoad]
  );

  const marginTopContent = useMemo(
    () =>
      isHeaderVisible
        ? layout.header.height + layout.subheader.height
        : layout.subheader.height,
    [isHeaderVisible]
  );

  return (
    <View className={styles.appContainer}>
      {isLoadProcess && <LoadingOverlay />}
      {isDRmode && (
        <Box className={styles.headerMessage} data-testid="disaster-message">
          {DISASTER_RECOVERY_HEADER_MESSAGE}
        </Box>
      )}
      <Header
        headerContent={
          <AppHeader
            depots={depots}
            disasterRecoveryAccess={disasterRecoveryAccess}
          />
        }
        subheaderContent={<SubHeader />}
        isHeaderVisible={isHeaderVisible}
        setIsHeaderVisible={setIsHeaderVisible}
      />

      <>
        <View
          className={styles.content}
          style={{
            marginTop: marginTopContent,
            transition: 'margin-top .2s',
          }}
        >
          {!showRouteDetail && (
            <>
              {routeId && (
                <ToggleRouteDetailButton isDetailVisible={showRouteDetail} />
              )}
              <RouteManagementButtons
                bottomOptionsVisible={isRouteDataLoaded}
                onSelectCentreMap={handleOnCenterMap}
              />
            </>
          )}
          {!!depots?.length && (
            <>
              <View className={styles.left}>
                <SectionMenu>
                  <Routes>
                    <Route
                      path={`${PAGE_START}/*`}
                      element={<SectionListContainer />}
                    />
                    <Route
                      path={ROUTES_PAGE}
                      element={<RouteListContainer />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to={PAGE_START} replace />}
                    />
                  </Routes>
                </SectionMenu>
              </View>
              <Filter activeRoute={routeId} />
            </>
          )}
          {showRouteDetail && <RouteDetail routeDetail={routeDetail} />}
          {routeId && routeDetail && <ScheduleRoute />}
        </View>
        <View className={styles.mapbox}>
          <Mapbox
            handleOnLoad={handleOnMapLoad}
            coordinates={coordinates}
            zoom={zoom}
          >
            <StopsMapLayer map={map} />
            <DepotMapLayer />
            {showDriverRouteOnMap && (
              <DriverMapLayer map={map} marginTop={marginTopContent} />
            )}
          </Mapbox>
        </View>
        <ModalsContainer />
      </>
      <Notifier />
      <ModalControlled
        ref={disasterRecoveryWarningRef}
        Component={DRWarningModal}
        renderWhenOpen
      />
      <RouteLeavingGuard shouldBlockNavigation={shouldBlockNavigation} />
    </View>
  );
};

export default compose(
  withTrack(trackProps(ROUTE_PAGE)),
  withTrackProps({ onMapLoad: MAP_BOX.LOAD })
)(PlannerPage);
